import i18n from '../../../i18n';

/**
 * Display Formatting Functions
 * Centralized formatters for consistent data presentation across the UI
 * Eliminates duplication of formatting logic throughout the application
 */

const getLocale = () => ({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES');

/**
 * Format monetary values with appropriate units and localization
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: EUR)
 * @param {string} locale - Locale for formatting (default: es-ES)
 * @returns {string} Formatted currency string
 */
export const formatRevenue = (amount, currency = 'EUR', locale = getLocale()) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '€0';
  }

  // Convert to appropriate units
  if (Math.abs(amount) >= 1000000) {
    return `€${(amount / 1000000).toFixed(1)}M`;
  } else if (Math.abs(amount) >= 1000) {
    return `€${(amount / 1000).toFixed(1)}K`;
  } else {
    return `€${amount.toFixed(0)}`;
  }
};

/**
 * Format dates with consistent styling
 * @param {Date|string|number} date - Date to format
 * @param {string} format - Format type ('short', 'long', 'relative', 'time')
 * @param {string} locale - Locale for formatting
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'short', locale = getLocale()) => {
  if (!date) return '-';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '-';

  const now = new Date();
  const diffInMs = now - dateObj;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  switch (format) {
    case 'relative':
      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
      if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
      return `${Math.floor(diffInDays / 365)} years ago`;

    case 'time':
      return dateObj.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit'
      });

    case 'long':
      return dateObj.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

    case 'short':
    default:
      return dateObj.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
  }
};

/**
 * Format duration/time spans
 * @param {number} weeks - Number of weeks
 * @param {Function} t - Translation function
 * @returns {string} Formatted duration string
 */
export const formatDuration = (weeks, t) => {
  if (!weeks || weeks === 0) {
    return t ? t('thisWeek') : 'This week';
  }

  if (weeks === 1) {
    return t ? t('oneWeekAgo') : '1 week ago';
  }

  if (weeks < 4) {
    return t ? t('weeksAgo', { count: weeks }) : `${weeks} weeks ago`;
  }

  if (weeks < 52) {
    const months = Math.floor(weeks / 4);
    return t ? t('monthsAgo', { count: months }) : `${months} months ago`;
  }

  const years = Math.floor(weeks / 52);
  return t ? t('yearsAgo', { count: years }) : `${years} years ago`;
};

/**
 * Format percentages with appropriate precision
 * @param {number} value - Percentage value (0-100)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  return `${value.toFixed(decimals)}%`;
};

/**
 * Format large numbers with appropriate units
 * @param {number} value - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(decimals)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(decimals)}K`;
  } else {
    return value.toFixed(decimals);
  }
};

/**
 * Format machine IDs for display
 * @param {string} machineId - Machine identifier
 * @returns {string} Formatted machine ID
 */
export const formatMachineId = (machineId) => {
  if (!machineId) return '-';
  
  // Add spacing for readability if it's alphanumeric
  return machineId.replace(/([A-Za-z])(\d)/g, '$1 $2');
};

/**
 * Format product names for consistent display
 * @param {string} productName - Product name
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Formatted product name
 */
export const formatProductName = (productName, maxLength = 30) => {
  if (!productName) return '-';
  
  // Clean up product name
  const cleaned = productName.trim();
  
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  
  return `${cleaned.substring(0, maxLength - 3)}...`;
};


/**
 * Format status values with appropriate styling
 * @param {boolean|string} status - Status value
 * @param {Function} t - Translation function
 * @returns {Object} Formatted status with color and label
 */
export const formatStatus = (status, t) => {
  const isResolved = status === true || status === 'resolved';
  
  return {
    label: isResolved ? 
      (t ? t('resolved') : 'Resolved') : 
      (t ? t('pending') : 'Pending'),
    color: isResolved ? '#22CC22' : '#FF8800',
    backgroundColor: isResolved ? '#E6FFE6' : '#FFF4E6'
  };
};

/**
 * Format file sizes
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) {
    return text || '';
  }
  
  return `${text.substring(0, maxLength - 3)}...`;
};

/**
 * Format arrays for display
 * @param {Array} array - Array to format
 * @param {number} maxItems - Maximum items to show
 * @param {string} separator - Separator between items
 * @returns {string} Formatted array string
 */
export const formatArray = (array, maxItems = 3, separator = ', ') => {
  if (!array || !Array.isArray(array) || array.length === 0) {
    return '-';
  }

  if (array.length <= maxItems) {
    return array.join(separator);
  }

  const remaining = array.length - maxItems;
  return `${array.slice(0, maxItems).join(separator)} +${remaining} more`;
};

export default {
  formatRevenue,
  formatDate,
  formatDuration,
  formatPercentage,
  formatNumber,
  formatMachineId,
  formatProductName,
  formatStatus,
  formatFileSize,
  truncateText,
  formatArray
};